import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { createProductSupplyV2, createProductV2, deleteProductByIdV2, deleteProductSupplyByIdV2, productsBulkCreate, updateProductByIdV2, createProductPriceOptionV2, updateProductPriceOptionV2, deleteProductPriceOptionV2 } from '../../services/products.service';
import { IProductInvoiceModuleSettings } from '../../interfaces/Product';
import { bulkUpdateProductPrices, productBulkImportByStoreV2, setOrderSequence } from '../../services/store.service';
import { orderSequenceKeys } from './queries';

const listQueryKey = 'products';
const orderSequenceQueryKey = "orderSequence";

export const useDeleteProductById = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await deleteProductByIdV2(id);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      price: number;
      expressPrice: number;
      type: 'normal' | 'weight' | 'length';
      minimumBillableQuantity?: number | null;
      pieces: number;
      sku?: string;
      overlayText: string;
      order: number;
      taxExemptOne: boolean;
      taxExemptTwo: boolean;
      taxExemptThree: boolean;
      showInApp: boolean;
      image: string | null;
      store: string | null;
      section: string;
      subcategory?: string | null;
      invoice_description: string;
      invoice_product_key: string;
      invoice_product_unit_key: string;
      invoice_product_unit_name: string;
      invoiceModuleSettings?: IProductInvoiceModuleSettings;
      productSupplies: Array<any>,
      isOutsourced?: boolean,
      outsourcingPartner?: string
    }) => {
      try {
        const res = await createProductV2(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: ['sections'] }),
      ]);
    }
  });
};

export const useUpdateProductById = (id: string, storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      name?: string;
      description?: string;
      price?: number;
      expressPrice?: number;
      type?: 'normal' | 'weight' | 'length';
      minimumBillableQuantity?: number | null;
      pieces?: number;
      sku?: string;
      overlayText?: string;
      order?: number;
      taxExemptOne?: boolean;
      taxExemptTwo?: boolean;
      taxExemptThree?: boolean;
      showInApp?: boolean;
      image?: string;
      invoice_product_key?: string,
      invoice_description?: string,
      invoice_product_unit_key?: string
      invoice_product_unit_name?: string
      invoiceModuleSettings?: IProductInvoiceModuleSettings
      isOutsourced?: boolean
      outsourcingPartner?: string
      subcategory?: string | null
      store?: string | null
    }) => {
      try {
        const res = await updateProductByIdV2(id, data);
        if (!res?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      //INVALIDATE CACHED  WITH STORE ID AND ID
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: ['sections'] }),
        queryClient.invalidateQueries({
          queryKey: [listQueryKey, { _id: id, storeId }],
        }),
      ]);
    }
  });
};

export const useCreateProductSupply = (id: string, storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      supplyId: string;
      usageAmount: number;
    }) => {
      try {
        const res = await createProductSupplyV2(storeId, id, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      }), queryClient.invalidateQueries({
        queryKey: [listQueryKey, { _id: id, storeId }],
      })]);
    }
  });
};

export const useDeleteProductSupplyById = (prodId: string, storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (prodSupplyId: string) => {
      try {
        const res = await deleteProductSupplyByIdV2(storeId, prodId, prodSupplyId);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      }), queryClient.invalidateQueries({
        queryKey: [listQueryKey, { _id: prodId, storeId }],
      })]);
    }
  });
};

/** @deprecated Legacy CSV product bulk-create flow. Use `useBulkImportProduct`. */
export const useBulkCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      storeId: string;
      formData: any;
    }) => {
      try {
        const res = await productsBulkCreate(data.storeId, data?.formData?.get('section'), data.formData);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['sections'], //we use sections here because we fetch products using sections services TODO: MAYBE NOT NEEDED ANY MORE SINCE WE HAVE PRODUCT SCHEMA NOW
      })]);
    }
  });
};

export const useBulkImportProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      storeId: string;
      formData: FormData;
    }) => {
      try {
        const res = await productBulkImportByStoreV2(data.storeId, data.formData);
        if (!res) {
          throw new Error('Oh no! Something went wrong while importing.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};

/** @deprecated Use `useBulkImportProduct`. */
export const useBulkUpdateProduct = useBulkImportProduct;

// Price Options hooks
export const useCreateProductPriceOption = (productId: string, storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      label: string;
      description?: string;
      price: number;
      expressPrice: number;
      isDefault?: boolean;
      isActive?: boolean;
    }) => {
      try {
        const res = await createProductPriceOptionV2(productId, data);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating product price option.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [listQueryKey, { _id: productId, storeId }] }),
      ]);
    }
  });
};

export const useUpdateProductPriceOption = (productId: string, storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      optionId: string;
      data: {
        label?: string;
        description?: string;
        price?: number;
        expressPrice?: number;
        isDefault?: boolean;
        isActive?: boolean;
      };
    }) => {
      try {
        const res = await updateProductPriceOptionV2(productId, payload.optionId, payload.data);
        if (!res) {
          throw new Error('Oh no! Something went wrong while updating product price option.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [listQueryKey, { _id: productId, storeId }] }),
      ]);
    }
  });
};

export const useDeleteProductPriceOption = (productId: string, storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (optionId: string) => {
      try {
        const res = await deleteProductPriceOptionV2(productId, optionId);
        if (!res) {
          throw new Error('Oh no! Something went wrong while deleting product price option.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [listQueryKey, { _id: productId, storeId }] }),
      ]);
    }
  });
};

export const useBulkUpdateProductPrices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      storeId: string,
      section: 'all' | string;
      value: number;
    }) => {
      try {
        const res = await bulkUpdateProductPrices(data);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    }
  });
};



export const useSetOrderSequence = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { currentSequence: number }) => {
      try {
        const res = await setOrderSequence(storeId, data);

        // Ajusta según tu response real (si regresa res.data.data o res.data)
        if (!res?.data?.data) {
          throw new Error("Oh no! Something went wrong while updating order sequence.");
        }

        return res.data.data;
      } catch (e: any) {
        console.log("error caught", e?.response?.data?.errors);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        // refresca el GET de order-sequence para que el UI muestre el nuevo valor
        queryClient.invalidateQueries({
          queryKey: orderSequenceKeys.byStore(storeId),
        })

        // opcional: si tienes algún query de store settings general que muestre esto
        // queryClient.invalidateQueries({ queryKey: ["store", { _id: storeId }] }),
        // queryClient.invalidateQueries({ queryKey: ["stores"] }),
      ]);
    },
  });
};
